import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type AttendanceStatus   = 'Present' | 'Absent' | 'Late' | 'Half-Day' | 'Holiday' | 'Leave';
export type AttendanceType     = 'Student' | 'Staff';
export type LeaveStatus        = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';
export type LeaveType          = 'Sick Leave' | 'Casual Leave' | 'Medical Leave' | 'Emergency' | 'Other';
export type AttendanceTab      = 'mark' | 'records' | 'reports' | 'leaves';
export type RepFilter          = 'all' | 'above75' | 'below75' | 'below50';

export interface StudentRow {
  id:         string;
  name:       string;
  roll_no:    string;
  gender:     string;
  photo:      string;
  status:     AttendanceStatus;
  remark:     string;
  profile_photo: string;
}

export interface AttendanceSession {
  id:           number;
  att_date:     string;
  class_name:   string;
  section:      string;
  subject:      string;
  att_period:   string;
  marked_by:    string;
  total:        number;
  present:      number;
  absent:       number;
  late:         number;
  half_day:     number;
  on_leave:     number;
  is_holiday:   boolean;
  holiday_name: string;
}

export interface AttendanceRecord {
  id:          number;
  student_id:  string;
  student_name:string;
  class_name:  string;
  section:     string;
  subject:     string;
  att_period:  string;
  status:      AttendanceStatus;
  remark:      string;
  att_date:    string;
}

export interface MonthlyReport {
  student_id:    string;
  student_name:  string;
  roll_no:       string;
  class_name:    string;
  total_days:    number;
  present:       number;
  absent:        number;
  late:          number;
  half_day:      number;
  on_leave:      number;
  holidays:      number;
  percentage:    number;
}

export interface LeaveApplication {
  id:           number;
  student_id:   string;
  student_name: string;
  class_name:   string;
  roll_no:      string;
  leave_type:   LeaveType;
  from_date:    string;
  to_date:      string;
  total_days:   number;
  reason:       string;
  status:       LeaveStatus;
  applied_on:   string;
  reviewed_by:  string;
  review_remark:string;
  reviewed_on:  string;
}

export interface NoticeSent {
  sent_at: string;
  message: string;
}

@Component({
  selector:    'app-attendance',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './attendance.html',
  styleUrl:    './attendance.scss',
})
export class Attendance implements OnInit {
  dataService     = inject(DataService);
  loading = false;
  allStudents: any = [];
  students: any = [];
  studentZ: any = [];
  user: any = null;
  std_kw: string  = '';

  activeTab       = signal<AttendanceTab>('mark');
  saveSuccess     = signal('');
  showLeaveModal  = signal(false);
  viewingLeave    = signal<LeaveApplication | null>(null);

  // ── Mark Attendance state ─────────────────────────────
  markDate      = new Date().toISOString().substring(0, 10);
  markClass     = '';
  markSection   = '';
  markSubject   = '';
  markPeriod    = '';
  attendanceRows = signal<StudentRow[]>([]);
  sessionSaved  = false;
  isHoliday     = false;
  holidayName   = '';

  // ── Mark tab search ───────────────────────────────────
  markSearch = signal('');

  // ── Records filters (all signals for reactivity) ──────
  recClass    = signal('');
  recSection  = signal('');
  recStatus   = signal('');
  recSubject  = signal('');
  recDateFrom = signal('');
  recDateTo   = signal('');
  recSearch   = signal('');

  // ── Reports filters ───────────────────────────────────
  repClass    = signal('');
  repMonth    = signal('');
  repSearch   = signal('');
  repFilter   = signal<RepFilter>('all');

  // ── Leave filters ─────────────────────────────────────
  leaveSearch    = signal('');
  leaveClass     = signal('');
  leaveStatus    = signal('');
  leaveType      = signal('');
  leaveDateFrom  = signal('');
  leaveDateTo    = signal('');
  newLeave: Partial<LeaveApplication> = {};
  showAddLeave   = signal(false);
  leaveSubmitted = false;

  // ── Leave edit & inline reject ────────────────────────
  showEditLeaveModal = signal(false);
  editLeaveForm: Partial<LeaveApplication> = {};
  editLeaveSubmitted = false;
  showRejectModal    = signal<number | null>(null);
  rejectRemark       = '';

  // ── Notice modal ──────────────────────────────────────
  showNoticeModal   = signal(false);
  noticingStudent   = signal<MonthlyReport | null>(null);
  noticeMessage     = '';
  noticeSentMap     = signal<Record<string, NoticeSent>>({});

  // ── Lookups ───────────────────────────────────────────
  readonly classOptions  = this.dataService.classes;
  readonly sectionOpts   = this.dataService.sections;
  readonly subjectOpts   = this.dataService.subjects;
  readonly periodOpts    = ['1st Period','2nd Period','3rd Period','4th Period','5th Period','6th Period','7th Period','Assembly','All Periods'];
  readonly leaveTypes: LeaveType[] = ['Sick Leave','Casual Leave','Medical Leave','Emergency','Other'];

  // ── Saved sessions ────────────────────────────────────
  private nextSessionId = 6;
  sessions = signal<AttendanceSession[]>([
    // List for Records Tab
    // { id:1, att_date:'2026-07-28', class_name:'Class XI – Science', section:'A', subject:'Mathematics',   att_period:'1st Period',  marked_by:'Rajesh Kumar',  total:42, present:38, absent:3, late:1, half_day:0, on_leave:0, is_holiday:false, holiday_name:'' },
    // { id:2, att_date:'2026-07-28', class_name:'Class XI – Science', section:'A', subject:'Physics',       att_period:'2nd Period',  marked_by:'Priya Sharma',  total:42, present:39, absent:2, late:1, half_day:0, on_leave:0, is_holiday:false, holiday_name:'' },
    // { id:3, att_date:'2026-07-28', class_name:'Class IX',           section:'A', subject:'All Subjects',  att_period:'All Periods', marked_by:'Sunita Verma',  total:38, present:35, absent:2, late:0, half_day:1, on_leave:0, is_holiday:false, holiday_name:'' },
    // { id:4, att_date:'2026-07-29', class_name:'Class XI – Science', section:'A', subject:'All Subjects',  att_period:'All Periods', marked_by:'Rajesh Kumar',  total:42, present:40, absent:2, late:0, half_day:0, on_leave:0, is_holiday:false, holiday_name:'' },
    // { id:5, att_date:'2026-07-30', class_name:'Class XI – Science', section:'A', subject:'All Subjects',  att_period:'All Periods', marked_by:'Admin',         total:42, present:0,  absent:0, late:0, half_day:0, on_leave:0, is_holiday:true,  holiday_name:'Eid ul-Adha' },
    // { id:6, att_date:'2026-07-25', class_name:'Class IX',           section:'B', subject:'English',       att_period:'3rd Period',  marked_by:'Anita Roy',     total:36, present:32, absent:3, late:1, half_day:0, on_leave:0, is_holiday:false, holiday_name:'' },
    // { id:7, att_date:'2026-07-24', class_name:'BCA',                section:'A', subject:'Computer Science',att_period:'2nd Period',marked_by:'Harish Nair',   total:30, present:28, absent:2, late:0, half_day:0, on_leave:0, is_holiday:false, holiday_name:'' },
    // { id:8, att_date:'2026-07-23', class_name:'Class XI – Science', section:'A', subject:'Chemistry',     att_period:'4th Period',  marked_by:'Anjali Shah',   total:42, present:36, absent:4, late:2, half_day:0, on_leave:0, is_holiday:false, holiday_name:'' },
  ]);

  // ── Attendance records ────────────────────────────────
  private nextRecId = 100;
  records = signal<AttendanceRecord[]>([]);     //(this.buildRecords());

  // ── Monthly reports ───────────────────────────────────
  monthlyReports = signal<MonthlyReport[]>([]);     //(this.buildReports());

  // ── Leave applications ────────────────────────────────
  private nextLeaveId = 7;
  leaveApps = signal<LeaveApplication[]>([
    // List for Leaves Tab
    // { id:1, student_id:'S001', student_name:'Arjun Sharma',  class_name:'Class XI – Science', roll_no:'11-A-01', leave_type:'Sick Leave',    from_date:'2026-07-20', to_date:'2026-07-21', total_days:2, reason:'High fever and cold.',              status:'Approved', applied_on:'2026-07-19', reviewed_by:'Rajesh Kumar', review_remark:'Approved. Submit medical certificate on return.', reviewed_on:'2026-07-19' },
    // { id:2, student_id:'S003', student_name:'Rahul Kumar',   class_name:'Class IX',           roll_no:'9-A-07',  leave_type:'Casual Leave',  from_date:'2026-07-25', to_date:'2026-07-25', total_days:1, reason:'Family function.',                   status:'Approved', applied_on:'2026-07-24', reviewed_by:'Sunita Verma', review_remark:'Approved.',                                      reviewed_on:'2026-07-24' },
    // { id:3, student_id:'S006', student_name:'Kavita Yadav',  class_name:'Class XI – Science', roll_no:'11-A-06', leave_type:'Medical Leave', from_date:'2026-07-28', to_date:'2026-07-29', total_days:2, reason:'Dental surgery.',                    status:'Pending',  applied_on:'2026-07-27', reviewed_by:'',             review_remark:'',                                               reviewed_on:'' },
    // { id:4, student_id:'S009', student_name:'Pooja Nair',    class_name:'BCA',                roll_no:'BCA-1-09',leave_type:'Emergency',     from_date:'2026-07-31', to_date:'2026-08-02', total_days:3, reason:'Family emergency – father hospitalised.',status:'Approved',applied_on:'2026-07-31', reviewed_by:'Admin',        review_remark:'Approved with sympathy. Keep us posted.',        reviewed_on:'2026-07-31' },
    // { id:5, student_id:'S010', student_name:'Suresh Babu',   class_name:'Class IX',           roll_no:'9-B-12',  leave_type:'Sick Leave',    from_date:'2026-08-01', to_date:'2026-08-01', total_days:1, reason:'Stomach ache.',                      status:'Pending',  applied_on:'2026-07-31', reviewed_by:'',             review_remark:'',                                               reviewed_on:'' },
    // { id:6, student_id:'S002', student_name:'Priya Mehta',   class_name:'Class XI – Science', roll_no:'11-B-04', leave_type:'Casual Leave',  from_date:'2026-07-15', to_date:'2026-07-15', total_days:1, reason:'Out-of-station for cousin\'s wedding.', status:'Rejected',applied_on:'2026-07-14', reviewed_by:'Rajesh Kumar', review_remark:'Leave denied – pre-board exams in progress.',   reviewed_on:'2026-07-14' },
    // { id:7, student_id:'S016', student_name:'Ravi Gupta',    class_name:'Class XI – Science', roll_no:'11-A-07', leave_type:'Casual Leave',  from_date:'2026-07-10', to_date:'2026-07-12', total_days:3, reason:'Attending cousin\'s wedding out of town.',status:'Approved',applied_on:'2026-07-09', reviewed_by:'Rajesh Kumar', review_remark:'Approved.',                                      reviewed_on:'2026-07-09' },
    // { id:8, student_id:'S018', student_name:'Rahul Mishra',  class_name:'Class XI – Science', roll_no:'11-A-09', leave_type:'Sick Leave',    from_date:'2026-07-05', to_date:'2026-07-09', total_days:5, reason:'Typhoid fever – doctor\'s rest advised.', status:'Approved',applied_on:'2026-07-05', reviewed_by:'Admin',        review_remark:'Approved. Bring medical fitness certificate.',   reviewed_on:'2026-07-05' },
  ]);

  // ── Computed ──────────────────────────────────────────

  /** Reactive search filter on mark-attendance student grid */
  readonly filteredAttendanceRows = computed(() => {
    const q = this.markSearch().toLowerCase().trim();
    if (!q) return this.attendanceRows();
    return this.attendanceRows().filter(r =>
      r.name.toLowerCase().includes(q) || r.roll_no.toLowerCase().includes(q)
    );
  });

  readonly filteredSessions = computed(() => {
    const q = this.recSearch().toLowerCase();
    return this.sessions()
      .filter(s => {
        const matchClass   = !this.recClass()   || s.class_name === this.recClass();
        const matchSec     = !this.recSection() || s.section    === this.recSection();
        const matchSubj    = !this.recSubject() || s.subject    === this.recSubject();
        const matchDateF   = !this.recDateFrom()|| s.att_date >= this.recDateFrom();
        const matchDateT   = !this.recDateTo()  || s.att_date <= this.recDateTo();
        const matchStatus  = !this.recStatus()  ||
          (this.recStatus() === 'holiday' ? s.is_holiday : !s.is_holiday);
        const matchSearch  = !q ||
          s.class_name.toLowerCase().includes(q) ||
          s.subject.toLowerCase().includes(q) ||
          s.marked_by.toLowerCase().includes(q) ||
          s.section.toLowerCase().includes(q);
        return matchClass && matchSec && matchSubj && matchDateF && matchDateT && matchStatus && matchSearch;
      })
      .sort((a, b) => b.att_date.localeCompare(a.att_date));
  });

  readonly filteredReports = computed(() => {
    const q = this.repSearch().toLowerCase();
    return this.monthlyReports()
      .filter(r => {
        const matchClass  = r.class_name === this.repClass();
        const matchSearch = !q || r.student_name.toLowerCase().includes(q) || r.roll_no.toLowerCase().includes(q);
        const f = this.repFilter();
        const matchFilter =
          f === 'all'      ? true :
          f === 'above75'  ? r.percentage >= 75 :
          f === 'below75'  ? r.percentage < 75  :
          f === 'below50'  ? r.percentage < 50  : true;
        return matchClass && matchSearch && matchFilter;
      })
      .sort((a, b) => a.roll_no.localeCompare(b.roll_no));
  });

  readonly filteredLeaves = computed(() => {
    const q = this.leaveSearch().toLowerCase();
    return this.leaveApps()
      .filter(l => {
        const matchSearch = !q ||
          l.student_name.toLowerCase().includes(q) ||
          // l.student_id.toLowerCase().includes(q)   ||
          l.roll_no.toLowerCase().includes(q);
        const matchClass  = !this.leaveClass()     || l.class_name  === this.leaveClass();
        const matchStatus = !this.leaveStatus()    || l.status      === this.leaveStatus();
        const matchType   = !this.leaveType()      || l.leave_type  === this.leaveType();
        const matchDateF  = !this.leaveDateFrom()  || l.from_date  >= this.leaveDateFrom();
        const matchDateT  = !this.leaveDateTo()    || l.to_date    <= this.leaveDateTo();
        return matchSearch && matchClass && matchStatus && matchType && matchDateF && matchDateT;
      })
      .sort((a, b) => b.applied_on.localeCompare(a.applied_on));
  });

  readonly markSummary = computed(() => {
    const rows = this.attendanceRows();
    return {
      total:    rows.length,
      present:  rows.filter(r => r.status === 'Present').length,
      absent:   rows.filter(r => r.status === 'Absent').length,
      late:     rows.filter(r => r.status === 'Late').length,
      half_day: rows.filter(r => r.status === 'Half-Day').length,
      on_leave: rows.filter(r => r.status === 'Leave').length,
    };
  });

  readonly leaveSummary = computed(() => ({
    pending:  this.leaveApps().filter(l => l.status === 'Pending').length,
    approved: this.leaveApps().filter(l => l.status === 'Approved').length,
    rejected: this.leaveApps().filter(l => l.status === 'Rejected').length,
    total:    this.leaveApps().length,
  }));

  readonly repSummary = computed(() => {
    const list: any = this.monthlyReports().filter(r => r.class_name === this.repClass());
    if (!list.length) return { avg: 0, above75: 0, below75: 0, below50: 0 };
    const avg = Math.round(list.reduce((s: any, r: any) => s + r.percentage, 0) / list.length);
    return {
      avg,
      above75: list.filter((r: any) => r.percentage >= 75).length,
      below75: list.filter((r: any) => r.percentage < 75 && r.percentage >= 50).length,
      below50: list.filter((r: any) => r.percentage < 50).length,
    };
  });

  readonly lowAttendanceStudents = computed(() =>
    this.filteredReports().filter(r => r.percentage < 75)
  );

  readonly noticeCount = computed(() =>
    Object.keys(this.noticeSentMap()).length
  );

  async ngOnInit() {
    // this.newLeave.from_date = '2026-08-17'; 
    // this.newLeave.to_date = '2026-08-17';

    let erp_rem: any = localStorage.getItem("erp_rem");

    if(erp_rem && !this.user) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
    }

    this.loading = true;
    await this.dataService.getAllRecords('std_leaves').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.leaveApps.set(res);
      }
    });

    this.loading = true;
    let retVal: any = [];
    await this.dataService.getAllRecords('admissions').then((res: any) => {
      if(res && res.length) {
        for(let i=0; i<res.length; i++) {
          res[i].std_info = this.dataService.str2json(res[i].std_info);

          const std: any = {
            id: res[i].id,
            name: res[i].std_name,
            class_name: res[i].class_name,
            section: res[i].section,
            roll_no: res[i].roll_no,
            gender: res[i].std_info[0].gender,
            profile_photo: res[i].profile_photo
          };
          this.allStudents.push(std);
        }

        this.students = Object.assign([], this.allStudents);
      }

      this.loading = false;
    });

    this.loading = true;
    await this.dataService.getAllRecords('std_monthly_rpt').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        for(let i=0; i<res.length; i++) {
          if(res[i].present==0) {
            res[i].present = res[i].total_days;
          }

          res[i].percentage = (res[i].total_days / res[i].present) * 100;
        }

        this.monthlyReports.set(res);
      }
      this.loading = false;
    });


    this.loading = true;
    let retVal3: any = [];
    await this.dataService.getAllRecords('std_att_rec').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        for(let i=0; i<res.length; i++) {
          const std3: any = { 
            id: res[i].id, 
            att_date: res[i].att_date, 
            class_name: res[i].class_name, 
            section: res[i].class_section, 
            subject: res[i].subject_name, 
            att_period: res[i].att_period, 
            marked_by: res[i].marked_by, 
            total: res[i].total, 
            present: res[i].present, 
            absent: res[i].absent, 
            late: res[i].late, 
            half_day: res[i].half_day, 
            on_leave: res[i].leave, 
            is_holiday: res[i].is_holiday, 
            holiday_name: res[i].holiday_name
          };
          retVal3.push(std3);
        }

        this.sessions.set(retVal3);
      }
    });

    this.loadStudents(false); 
  }

  loadStudentDetails(): void {
    if (!this.std_kw) return;
    let student = this.allStudents.filter((data: any) => {
      return data.roll_no == this.std_kw || data.name.toLowerCase() == this.std_kw.toLowerCase();
    });

    if (student && student.length) {
      student = student[0];

      this.newLeave.student_id = student.id;
      this.newLeave.student_name = student.name;
      this.newLeave.class_name = student.class_name;
      this.newLeave.roll_no = student.roll_no;
      // this.loadFeeItems();
      // this.studentLoaded = true;
    } else {
      this.newLeave.student_id = '';
      this.newLeave.student_name = '';
      this.newLeave.class_name = '';
      this.newLeave.roll_no = '';
      // this.studentLoaded = false;
    }
  }

  // ── MARK ATTENDANCE ───────────────────────────────────
  async loadStudents(tf: boolean) {
    if(!this.markClass || this.markClass=="") return;

    if(tf) {
      this.loading = true;

      let attData: any = {
        "att_date": this.markDate,
        "class_name": this.markClass,
        "class_section": this.markSection,
        "subject_name": this.markSubject,
        "att_period": this.markPeriod
      };
      const retVal: any = await this.dataService.getAllRecords('std_attendanZ', attData).then((res: any) => {
        this.loading = false;
        if(res && res.length) {
          this.studentZ = [];

          for(let i=0; i<res.length; i++) {
            res[i].std_info = res[i].std_info ? this.dataService.str2json(res[i].std_info) : '';

            const std: any = {
              id: res[i].student_id,
              name: res[i].std_name || '',
              roll_no: 'none',
              gender: res[i]?.std_info[0]?.gender || '',
              photo: res[i].profile_photo, 
              status: res[i].att_status, 
              remark: res[i].remark
            };
            this.studentZ.push(std);
          }

          this.loading = false;
        } else {
          tf = false;
        }
      });
    }

    const STUDENTS: Record<string, { id: string; name: string; roll_no: string; gender: string, profile_photo: string }[]> = {
      // '10c': [
      //   {id:'S001',name:'Arjun Sharma',   roll_no:'11-A-01',gender:'Male'},
      //   {id:'S002',name:'Priya Mehta',    roll_no:'11-A-02',gender:'Female'},
      //   {id:'S006',name:'Kavita Yadav',   roll_no:'11-A-03',gender:'Female'},
      //   {id:'S013',name:'Meena Sharma',   roll_no:'11-A-04',gender:'Female'},
      //   {id:'S014',name:'Vikram Patel',   roll_no:'11-A-05',gender:'Male'},
      //   {id:'S015',name:'Sunita Dubey',   roll_no:'11-A-06',gender:'Female'},
      //   {id:'S016',name:'Ravi Gupta',     roll_no:'11-A-07',gender:'Male'},
      //   {id:'S017',name:'Ananya Kapoor',  roll_no:'11-A-08',gender:'Female'},
      //   {id:'S018',name:'Rahul Mishra',   roll_no:'11-A-09',gender:'Male'},
      //   {id:'S019',name:'Deepak Singh',   roll_no:'11-A-10',gender:'Male'},
      // ],
      // '11c': [
      //   {id:'S003',name:'Rahul Kumar',    roll_no:'9-A-01',gender:'Male'},
      //   {id:'S005',name:'Vikram Singh',   roll_no:'9-A-02',gender:'Male'},
      //   {id:'S010',name:'Suresh Babu',    roll_no:'9-A-03',gender:'Male'},
      //   {id:'S020',name:'Pooja Devi',     roll_no:'9-A-04',gender:'Female'},
      //   {id:'S021',name:'Anil Kumar',     roll_no:'9-A-05',gender:'Male'},
      //   {id:'S022',name:'Radha Singh',    roll_no:'9-A-06',gender:'Female'},
      //   {id:'S023',name:'Suraj Yadav',    roll_no:'9-A-07',gender:'Male'},
      //   {id:'S024',name:'Nisha Sharma',   roll_no:'9-A-08',gender:'Female'},
      // ],
      // '12c': [
      //   {id:'S004',name:'Sneha Patel',    roll_no:'BCA-01',gender:'Female'},
      //   {id:'S007',name:'Mohit Gupta',    roll_no:'BCA-02',gender:'Male'},
      //   {id:'S009',name:'Pooja Nair',     roll_no:'BCA-03',gender:'Female'},
      //   {id:'S025',name:'Rohan Verma',    roll_no:'BCA-04',gender:'Male'},
      //   {id:'S026',name:'Simran Kaur',    roll_no:'BCA-05',gender:'Female'},
      //   {id:'S027',name:'Harsh Agarwal',  roll_no:'BCA-06',gender:'Male'},
      // ],
      // '12c': this.students
    };

    this.students = Object.assign([], this.allStudents);

    if(this.markClass) {
      this.students = this.students?.filter((std: any) => {
        return std.class_name == this.markClass;
      });
    }
    if(this.markSection) {
      this.students = this.students?.filter((std: any) => {
        return std.section == this.markSection;
      });
    }

    STUDENTS[this.markClass] = this.students;
    const list = STUDENTS[this.markClass] ?? [];

    if(tf) {
      if(this.studentZ && this.studentZ.length == 1) {
        if(this.studentZ[0].status == "Holiday") {
          this.isHoliday = true;
          this.holidayName = this.studentZ[0].remark;
        }
      } else if(this.studentZ && this.studentZ.length > 1) {
        this.isHoliday = false;
        this.holidayName = '';

        let array1: any = [...list];
        let array2: any = [...this.studentZ];

        // 1. Create a lookup map from the second array (the updates) for quick access
        const updateMap: any = new Map(array2.map((item: any) => [item.id, item]));

        // 2. Map over the first array (20 records) to merge and update it
        const mergedArray: any = array1.map((item: any) => {
          item.status = 'Present';
          // If a matching record exists in the second array, use it; otherwise, keep the original
          return updateMap.get(item.id) || item;
        });

        this.attendanceRows.set(mergedArray);
      } else {
        this.isHoliday = false;
        this.holidayName = '';
      }
    } else {
      this.isHoliday = false;
      this.holidayName = '';

      this.attendanceRows.set(list.map((s: any) => ({
        ...s, photo: s.profile_photo, status: s.status || 'Present' as AttendanceStatus, remark: s.remark || '',
      })));
    }

    this.markSearch.set('');
    this.sessionSaved = false;
  }

  setAllStatus(status: AttendanceStatus): void {
    this.attendanceRows.update(rows => rows.map(r => ({ ...r, status })));
  }

  setStatus(id: string, status: AttendanceStatus): void {
    this.attendanceRows.update(rows => rows.map(r => r.id === id ? { ...r, status } : r));
  }

  saveAttendance(): void {
    if (this.isHoliday && !this.holidayName.trim()) { alert('Please enter the holiday name.'); return; }
    const s = this.markSummary();
    const newSession: AttendanceSession = {
      id:          this.nextSessionId++,
      att_date:    this.markDate,
      class_name:  this.markClass,
      section:     this.markSection,
      subject:     this.markSubject,
      att_period:  this.markPeriod,
      marked_by:   this.user.display_name,
        total:       s.total,
        present:     this.isHoliday ? 0 : s.present,
        absent:      this.isHoliday ? 0 : s.absent,
        late:        this.isHoliday ? 0 : s.late,
        half_day:    this.isHoliday ? 0 : s.half_day,
        on_leave:    this.isHoliday ? 0 : s.on_leave,
      is_holiday:  this.isHoliday,
      holiday_name:this.holidayName,
    };
    this.sessions.update(list => [newSession, ...list]);

    let newRecs: AttendanceRecord[];
    let newAtt: any = [];

    if (!this.isHoliday) {
      newRecs = this.attendanceRows().map(row => ({
        id:           this.nextRecId++,
        student_id:   row.id,
        student_name: row.name,
        class_name:   this.markClass,
        section:      this.markSection,
        att_date:     this.markDate,
        status:       row.status,
        remark:       row.remark,
        att_period:   this.markPeriod,
        subject:      this.markSubject,
      }));
      this.records.update(list => [...newRecs, ...list]);

      newAtt = newRecs.map((row: any) => ({
        att_date: this.markDate, 
        student_id: this.isHoliday ? 0 : row.student_id, 
        class_name: this.markClass, 
        class_section: this.markSection, 
        subject_name: this.markSubject, 
        att_period: this.markPeriod, 
        att_status: row.status, 
        remark: this.isHoliday ? '' : row.remark, 
        is_holiday: this.isHoliday, 
        holiday_name: this.holidayName, 
        marked_by: this.user.id
      }));
    } else {
      newAtt = [{
        att_date: this.markDate, 
        class_name: this.markClass, 
        class_section: '', 
        subject_name: '', 
        att_period: '', 
        is_holiday: this.isHoliday, 
        holiday_name: this.holidayName, 
        marked_by: this.user.id,
        student_id: 0, 
        att_status: 'Holiday', 
        remark: this.holidayName || 'Holiday'
      }];
    }

    // let newAtt: any = newRecs;
    /*if (!this.isHoliday) {
      // remove filter if need to records all students data,
      // currently getting only UnPresent attendances only.
      newAtt = this.attendanceRows()
      .filter((row: any) => row.status !== 'Present')
      .map((row: any) => ({
        att_date: this.markDate, 
        student_id: this.isHoliday ? 0 : row.id, 
        class_name: this.markClass, 
        class_section: this.markSection, 
        subject_name: this.markSubject, 
        att_period: this.markPeriod, 
        att_status: row.status, 
        remark: this.isHoliday ? '' : row.remark, 
        is_holiday: this.isHoliday, 
        holiday_name: this.holidayName, 
        marked_by: this.user.id
      }));
    } else {
      newAtt = [{
        att_date: this.markDate, 
        class_name: this.markClass, 
        class_section: '', 
        subject_name: '', 
        att_period: '', 
        is_holiday: this.isHoliday, 
        holiday_name: this.holidayName, 
        marked_by: this.user.id,
        student_id: 0, 
        att_status: 'Holiday', 
        remark: this.holidayName || 'Holiday'
      }];
    }*/

    this.doSave('std_att_bulk', newAtt);

    for(let i=0; i<newAtt.length; i++) {
      const std_id: number = newAtt[i].student_id;
      let std: any = this.allStudents.filter((obj: any)=>{
        return obj.id == std_id;
      })[0];

      if(std) {
        // set activity log details
				this.dataService.setActivityLog('create', 'attendance', 'Attendance of "'+std.name+' (#'+std.roll_no+'), '+this.dataService.getClassName(std.class_name)+' class" marked as '+newAtt[i].att_status+'.');
      }
    }

    this.sessionSaved = true;
    this.flash(`Attendance saved for ${this.markClass} – ${this.markSection} (${this.markDate})`);
  }

  // ── LEAVE EDIT ────────────────────────────────────────
  openEditLeave(leave: LeaveApplication): void {
    this.editLeaveForm = { ...leave };
    this.editLeaveSubmitted = false;
    this.showEditLeaveModal.set(true);
  }

  saveEditLeave(): void {
    this.editLeaveSubmitted = true;
    const f = this.editLeaveForm;
    if (!f.student_name?.trim() || !f.from_date || !f.to_date || !f.reason?.trim()) return;
    const days = Math.max(1,
      Math.ceil((new Date(f.to_date).getTime() - new Date(f.from_date).getTime()) / 86400000) + 1
    );
    const id = f.id!;
    this.leaveApps.update(list => list.map(l => l.id === id ? {
      ...l,
      student_name:  f.student_name!,
      class_name:    f.class_name   ?? l.class_name,
      roll_no:       f.roll_no      ?? l.roll_no,
      leave_type:    f.leave_type   ?? l.leave_type,
      from_date:     f.from_date!,
      to_date:       f.to_date!,
      total_days:    days,
      reason:        f.reason!,
      review_remark: f.review_remark ?? l.review_remark,
    } : l));
    this.showEditLeaveModal.set(false);
    this.flash('Leave application updated.');
  }

  // ── INLINE APPROVE / REJECT ───────────────────────────
  quickApprove(id: number): void {
    const now = new Date().toISOString().substring(0, 10);
    this.leaveApps.update(list => list.map(l => l.id === id
      ? { ...l, status: 'Approved', reviewed_by: this.user.display_name, review_remark: 'Approved.', reviewed_on: now }
      : l
    ));
    this.flash('Leave approved.');
  }

  openQuickReject(id: number): void {
    this.showRejectModal.set(id);
    this.rejectRemark = '';
  }

  confirmQuickReject(): void {
    const id = this.showRejectModal();
    if (!id || !this.rejectRemark.trim()) return;
    const now = new Date().toISOString().substring(0, 10);
    this.leaveApps.update(list => list.map(l => l.id === id
      ? { ...l, status: 'Rejected', reviewed_by: this.user.display_name, review_remark: this.rejectRemark, reviewed_on: now }
      : l
    ));
    this.showRejectModal.set(null);
    this.flash('Leave rejected.');
  }

  // ── LEAVE (existing) ──────────────────────────────────
  openLeaveDetail(leave: LeaveApplication): void {
    this.viewingLeave.set({ ...leave });
    this.showLeaveModal.set(true);
  }

  approveLeave(lvData: any): void {
    lvData = { ...lvData, status: 'Approved', reviewed_by: this.user.display_name, review_remark: lvData.review_remark || 'Approved.', reviewed_on: new Date().toISOString().substring(0,10) };

    this.leaveApps.update(list =>
      list.map(l => l.id === lvData.id ? lvData : l)
    );

    this.doUpdate('std_leaves', lvData);

    this.showLeaveModal.set(false);
    this.flash('Leave approved successfully.');
  }

  rejectLeave(lvData: any): void {
    if (!lvData.review_remark.trim()) { alert('Please provide a reason for rejection.'); return; }
    lvData = { ...lvData, status: 'Rejected', reviewed_by: this.user.display_name, reviewed_on: new Date().toISOString().substring(0,10) };

    this.leaveApps.update(list =>
      list.map(l => l.id === lvData.id ? lvData : l)
    );

    this.doUpdate('std_leaves', lvData);

    this.showLeaveModal.set(false);
    this.flash('Leave rejected.');
  }

  submitNewLeave(): void {
    this.leaveSubmitted = true;
    const f = this.newLeave;
    if (!f.student_id || !f.student_name || !f.class_name || !f.leave_type || !f.from_date || !f.to_date || !f.reason) return;
    const days = Math.ceil((new Date(f.to_date!).getTime() - new Date(f.from_date!).getTime()) / 86400000) + 1;
    const lvData: any = {
      // id:           this.nextLeaveId++,
      id:           f.id!,
      student_id:   f.student_id!,
      student_name: f.student_name!,
      class_name:   f.class_name!,
      roll_no:      f.roll_no ?? '—',
      leave_type:   f.leave_type!,
      from_date:    f.from_date!,
      to_date:      f.to_date!,
      total_days:   days,
      reason:       f.reason!,
      status:       'Pending',
      applied_on:   new Date().toISOString().substring(0,10),
      reviewed_by:  '', review_remark: '', reviewed_on: '',
    };

    this.doSave('std_leaves', lvData);

    this.leaveApps.update(list => [lvData, ...list]);
    this.newLeave = {};
    this.leaveSubmitted = false;
    this.showAddLeave.set(false);
    this.flash('Leave application submitted.');
  }

  // ── NOTICE ────────────────────────────────────────────
  openNoticeModal(student: MonthlyReport): void {
    this.noticingStudent.set(student);
    this.noticeMessage = this.buildNoticeTemplate(student);
    this.showNoticeModal.set(true);
  }

  buildNoticeTemplate(r: MonthlyReport): string {
    const month = this.repMonth();
    const [yr, mo] = month.split('-');
    const monthName = new Date(Number(yr), Number(mo) - 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
    return [
      `Dear Parent / Guardian of ${r.student_name},`,
      ``,
      `Subject: Low Attendance Notice – ${monthName}`,
      ``,
      `This is to formally inform you that the attendance of your ward:`,
      ``,
      `  Name   : ${r.student_name}`,
      `  Class  : ${r.class_name}`,
      `  Roll No: ${r.roll_no}`,
      ``,
      `for the month of ${monthName} stands at ${r.percentage}%, which is below the required minimum of 75%.`,
      ``,
      `Attendance Summary:`,
      `  Working Days : ${r.total_days}`,
      `  Present      : ${r.present}`,
      `  Absent       : ${r.absent}`,
      `  Late          : ${r.late}`,
      `  Half-Day     : ${r.half_day}`,
      ``,
      `As per school regulations, a student with attendance below 75% may be barred from sitting for the examination. Kindly ensure regular and punctual attendance going forward.`,
      ``,
      `We request you to meet the Class Teacher or Administration at the earliest to discuss this matter.`,
      ``,
      `Regards,`,
      this.user?.display_name,
      this.user?.institute?.inst_name,
    ].join('\n');
  }

  sendNotice(): void {
    const student = this.noticingStudent();
    if (!student) return;
    this.noticeSentMap.update(m => ({
      ...m,
      [student.student_id]: { sent_at: new Date().toISOString(), message: this.noticeMessage },
    }));
    this.showNoticeModal.set(false);
    this.flash(`Notice sent to parents of ${student.student_name}.`);
  }

  sendAllNotices(): void {
    const low = this.lowAttendanceStudents();
    const map = this.noticeSentMap();
    const now = new Date().toISOString();
    const updates: Record<string, NoticeSent> = {};
    let newCount = 0;
    low.forEach(r => {
      if (!map[r.student_id]) {
        updates[r.student_id] = { sent_at: now, message: this.buildNoticeTemplate(r) };
        newCount++;
      }
    });
    if (!newCount) { this.flash('All notices already sent.'); return; }
    this.noticeSentMap.update(m => ({ ...m, ...updates }));
    this.flash(`Attendance notices sent to ${newCount} parent(s).`);
  }

  isNoticeSent(student_id: string): boolean {
    return !!this.noticeSentMap()[student_id];
  }

  getNoticeSentTime(student_id: string): string {
    const r = this.noticeSentMap()[student_id];
    if (!r) return '';
    return new Date(r.sent_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
  }

  // ── HELPERS ───────────────────────────────────────────
  getStatusClass(s: AttendanceStatus): string {
    return { Present:'att-present', Absent:'att-absent', Late:'att-late', 'Half-Day':'att-halfday', Holiday:'att-holiday', Leave:'att-leave' }[s] ?? '';
  }

  getStatusIcon(s: AttendanceStatus): string {
    return { Present:'bi-check-circle-fill', Absent:'bi-x-circle-fill', Late:'bi-clock-fill', 'Half-Day':'bi-circle-half', Holiday:'bi-sun-fill', Leave:'bi-calendar-event-fill' }[s] ?? 'bi-circle';
  }

  getLeaveStatusClass(s: LeaveStatus): string {
    return { Pending:'ls-pending', Approved:'ls-approved', Rejected:'ls-rejected', Cancelled:'ls-cancelled' }[s] ?? '';
  }

  attendancePct(s: AttendanceSession): number {
    if (!s.total || s.is_holiday) return 0;
    return Math.round((s.present / s.total) * 100);
  }

  reportPct(s: any): number {
    if (!s.total_days || s.is_holiday) return 0;
    return Math.round((s.present / s.total_days) * 100);
  }

  pctColor(pct: number): string {
    if (pct >= 90) return '#22c55e';
    if (pct >= 75) return '#f59e0b';
    if (pct >= 50) return '#ea580c';
    return '#ef4444';
  }

  formatDate(iso: string): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
  }

  getDay(iso: string): string {
    return new Date(iso).toLocaleDateString('en-IN', { weekday:'short' });
  }

  private flash(msg: string): void {
    this.saveSuccess.set(msg);
    setTimeout(() => this.saveSuccess.set(''), 3000);
  }

  resetRecordFilters(): void {
    this.recClass.set('');   this.recSection.set('');  this.recSubject.set('');
    this.recStatus.set('');  this.recDateFrom.set(''); this.recDateTo.set('');
    this.recSearch.set('');
  }

  resetRepFilters(): void {
    this.repSearch.set('');
    this.repFilter.set('all');
  }

  resetLeaveFilters(): void {
    this.leaveSearch.set('');   this.leaveClass.set('');
    this.leaveStatus.set('');   this.leaveType.set('');
    this.leaveDateFrom.set(''); this.leaveDateTo.set('');
  }

  // ── MOCK DATA ─────────────────────────────────────────
  // private buildRecords(): AttendanceRecord[] {
  //   const recs: AttendanceRecord[] = [];
  //   const students = [
  //     {id:'S001',name:'Arjun Sharma',  class_name:'Class XI – Science',section:'A'},
  //     {id:'S002',name:'Priya Mehta',   class_name:'Class XI – Science',section:'A'},
  //     {id:'S003',name:'Rahul Kumar',   class_name:'Class IX',          section:'A'},
  //     {id:'S006',name:'Kavita Yadav',  class_name:'Class XI – Science',section:'A'},
  //     {id:'S009',name:'Pooja Nair',    class_name:'BCA',               section:'A'},
  //     {id:'S016',name:'Ravi Gupta',    class_name:'Class XI – Science',section:'A'},
  //     {id:'S018',name:'Rahul Mishra',  class_name:'Class XI – Science',section:'A'},
  //   ];
  //   const dates   = ['2026-07-28','2026-07-27','2026-07-25','2026-07-24','2026-07-23','2026-07-22'];
  //   const statuses: AttendanceStatus[] = ['Present','Present','Present','Absent','Present','Late'];
  //   let id = 1;
  //   dates.forEach((date, di) => {
  //     students.forEach(st => {
  //       recs.push({ id:id++, student_id:st.id, student_name:st.name, class_name:st.class_name, section:st.section, att_date:date, status:statuses[di % statuses.length], remark:'', att_period:'All Periods', subject:'All Subjects' });
  //     });
  //   });
  //   return recs;
  // }

  // private buildReports(): MonthlyReport[] {
  //   // List for Reports Tab
  //   return [
  //     { student_id:'S001', student_name:'Arjun Sharma',   roll_no:'11-A-01', class_name:'Class XI – Science', total_days:26, present:24, absent:1, late:1, half_day:0, on_leave:0, holidays:3, percentage:92 },
  //     { student_id:'S002', student_name:'Priya Mehta',    roll_no:'11-A-02', class_name:'Class XI – Science', total_days:26, present:25, absent:0, late:0, half_day:1, on_leave:0, holidays:3, percentage:96 },
  //     { student_id:'S006', student_name:'Kavita Yadav',   roll_no:'11-A-03', class_name:'Class XI – Science', total_days:26, present:20, absent:4, late:1, half_day:1, on_leave:0, holidays:3, percentage:77 },
  //     { student_id:'S013', student_name:'Meena Sharma',   roll_no:'11-A-04', class_name:'Class XI – Science', total_days:26, present:19, absent:5, late:1, half_day:1, on_leave:0, holidays:3, percentage:73 },
  //     { student_id:'S014', student_name:'Vikram Patel',   roll_no:'11-A-05', class_name:'Class XI – Science', total_days:26, present:26, absent:0, late:0, half_day:0, on_leave:0, holidays:3, percentage:100 },
  //     { student_id:'S015', student_name:'Sunita Dubey',   roll_no:'11-A-06', class_name:'Class XI – Science', total_days:26, present:22, absent:3, late:0, half_day:1, on_leave:0, holidays:3, percentage:85 },
  //     { student_id:'S016', student_name:'Ravi Gupta',     roll_no:'11-A-07', class_name:'Class XI – Science', total_days:26, present:14, absent:10,late:1, half_day:1, on_leave:0, holidays:3, percentage:54 },
  //     { student_id:'S017', student_name:'Ananya Kapoor',  roll_no:'11-A-08', class_name:'Class XI – Science', total_days:26, present:23, absent:2, late:0, half_day:1, on_leave:0, holidays:3, percentage:88 },
  //     { student_id:'S018', student_name:'Rahul Mishra',   roll_no:'11-A-09', class_name:'Class XI – Science', total_days:26, present:11, absent:12,late:2, half_day:1, on_leave:0, holidays:3, percentage:42 },
  //     { student_id:'S019', student_name:'Deepak Singh',   roll_no:'11-A-10', class_name:'Class XI – Science', total_days:26, present:24, absent:1, late:1, half_day:0, on_leave:0, holidays:3, percentage:92 },
  //   ];
  // }


  // ── ACTIONS ─────────────────────────────────────────
  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
